import React, { FC, useRef } from 'react';
import { Control, Path } from 'react-hook-form';

import { ChevronDownIcon } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  ComboboxOptionsPortalWrapper,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../../../../components/combobox/index';
import { ShareClass as ShareClassHook } from '../../../../../../../hooks/index';
import { ShareClass } from '../../../../../../../types/share-classes.types';
import { FundraisingFormSchema } from '../../../../fundraising-validation';
import { ShareClassValue } from './share-class.types';

export type ShareClassesItemsListProps = {
  control: Control<FundraisingFormSchema>;
  data: ShareClass[];
  filedName: Path<FundraisingFormSchema>;
  isLoading: boolean;
};

const ShareClassesItemsList: FC<ShareClassesItemsListProps> = ({
  data,
  control,
  isLoading,
  filedName,
}) => {
  if (!data || isLoading)
    return (
      <span>
        <AppFormattedMessage id="LOADING" />
      </span>
    );
  return (
    <>
      {data.map(({ name, id }, i) => (
        <FormComboboxOption
          control={control}
          key={`${name}_${i}`}
          name={filedName as keyof FundraisingFormSchema}
          value={{ id, name } as unknown as FundraisingFormSchema[keyof FundraisingFormSchema]}
        >
          <span className="text-sm text-gray-700">{name}</span>
        </FormComboboxOption>
      ))}
    </>
  );
};

export type ShareClassComboboxProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  name: Path<FundraisingFormSchema>;
  onChange?: (shareClass: { id: string; name: string }) => void;
};

export const ShareClassCombobox: FC<ShareClassComboboxProps> = ({
  control,
  companyId,
  name,
  onChange,
}) => {
  const { shareClasses, isLoading } = ShareClassHook.useShareClasses({ companyId, filter: 'all' });
  const inputRef = useRef<HTMLDivElement | null>(null);

  return (
    <FormCombobox
      className="relative w-full"
      control={control}
      name={name as keyof FundraisingFormSchema}
      onSelect={(shareClass) => onChange?.(shareClass as { id: string; name: string })}
    >
      <div className="relative w-full" ref={inputRef}>
        <FormComboboxInput
          className={'bg-gray-900'}
          control={control}
          customValue={(value) => (value as ShareClassValue)?.name}
          icon={<ChevronDownIcon className="mt-2" />}
          name={name as keyof FundraisingFormSchema}
          readOnly
          wrapperClassName="h-10"
        />
        <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
      </div>
      <ComboboxOptionsPortalWrapper anchorRef={inputRef} itemsCount={shareClasses?.length || 0}>
        <ComboboxOptions className="z-20 h-fit max-h-[150px] w-full overflow-x-hidden">
          <ShareClassesItemsList
            control={control}
            data={shareClasses || []}
            filedName={name as keyof FundraisingFormSchema}
            isLoading={isLoading}
          />
        </ComboboxOptions>
      </ComboboxOptionsPortalWrapper>
    </FormCombobox>
  );
};
